import { useNavigate } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthEntryCard } from '@/features/auth/components/AuthEntryCard'
import { RegistrationBackButton } from '@/features/auth/components/RegistrationBackButton'
import { Button } from '@/shared/components/ui/Button'
import { registrationService } from '@/features/auth/services/registration.service'
import { getRegistrationState } from '@/features/auth/store/registration.store'

const VALID_SYMBOLS = /[-@!"#$*%&/=?]/

function getStrengthColor(completedRules: number) {
  if (completedRules >= 4) return 'success'
  if (completedRules >= 2) return 'warning'
  return 'danger'
}

export function AuthPasswordSetupPage() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('register')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showRegisterErrorModal, setShowRegisterErrorModal] = useState(false)

  const passwordRules = useMemo(() => [
    {
      id: 'length',
      label: t('register:password.rule.length'),
      test: (value: string) => value.length >= 8 && value.length <= 16,
    },
    {
      id: 'number',
      label: t('register:password.rule.number'),
      test: (value: string) => /\d/.test(value),
    },
    {
      id: 'symbol',
      label: t('register:password.rule.symbol'),
      test: (value: string) => VALID_SYMBOLS.test(value),
    },
    {
      id: 'uppercase',
      label: t('register:password.rule.uppercase'),
      test: (value: string) => /[A-Z]/.test(value),
    },
  ], [t])

  const ruleResults = useMemo(
    () =>
      passwordRules.map((rule) => ({
        ...rule,
        passed: rule.test(password),
      })),
    [password, passwordRules],
  )

  const completedRules = ruleResults.filter((rule) => rule.passed).length
  const strengthState = getStrengthColor(completedRules)
  const passwordsMatch = password.length > 0 && password === confirmPassword
  const passwordsMismatch = confirmPassword.length > 0 && !passwordsMatch
  const canContinue =
    completedRules === passwordRules.length && passwordsMatch && !isSubmitting

  async function handleContinue() {
    if (!canContinue) return

    const { correlationId, phone } = getRegistrationState()
    if (!correlationId) {
      navigate('/', { replace: true })
      return
    }

    setIsSubmitting(true)

    const result = await registrationService.completeProfile(correlationId, {
      phoneNumber: phone,
      password,
      passwordConfirmation: confirmPassword,
    }, i18n.language)

    setIsSubmitting(false)

    if (result.success) {
      navigate('/register/success', { replace: true })
      return
    }

    setShowRegisterErrorModal(true)
  }

  return (
    <>
      <AuthEntryCard variant="password">
        <section className="mt-4 w-full max-w-96 text-center">
          <h1 className="m-0 text-xl leading-tight font-bold text-text-primary">{t('register:password.title')}</h1>
          <p className="mt-3 text-sm leading-6 text-text-muted">
            {t('register:password.description')}
          </p>
        </section>

        <div className="mt-8 flex w-full max-w-96 flex-col gap-5">
          <label className="flex flex-col gap-2.5">
            <span className="text-[0.8125rem] leading-6 text-text-muted">{t('register:password.field.label')}</span>
            <span className="relative flex items-center">
              <input
                className="min-h-[2.125rem] w-full rounded border border-border-soft bg-surface-card px-3 py-[0.4375rem] pr-9 text-[0.8125rem] leading-6 text-text-primary transition-[border-color,box-shadow] duration-150 placeholder:text-[#99a7b5] focus-visible:border-brand-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring"
                maxLength={16}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={t('register:password.field.placeholder')}
                type={showPassword ? 'text' : 'password'}
                value={password}
              />
              <button
                aria-label={showPassword ? t('register:password.hide') : t('register:password.show')}
                className="absolute right-4 inline-flex items-center border-none bg-transparent p-0 text-[#606975] cursor-pointer focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                onClick={() => setShowPassword((current) => !current)}
                type="button"
              >
                ◉
              </button>
            </span>
          </label>

          <div className="h-2 w-full overflow-hidden rounded-full bg-[#e5edf4]">
            <span
              className={[
                'block h-full rounded-full transition-[width,background-color] duration-200',
                strengthState === 'success'
                  ? 'bg-[var(--color-success,#22c55e)]'
                  : strengthState === 'warning'
                    ? 'bg-[var(--color-warning,#f59e0b)]'
                    : 'bg-[#ff384d]',
              ].join(' ')}
              data-strength={strengthState}
              style={{
                width: `${Math.max((completedRules / passwordRules.length) * 100, 4)}%`,
              }}
            />
          </div>

          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {ruleResults.map((rule) => (
              <li
                key={rule.id}
                className="flex items-center gap-2 text-sm leading-5 text-text-muted"
                data-passed={rule.passed}
              >
                <span className={rule.passed ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#e8f8ef] text-xs text-[var(--color-success,#22c55e)]' : 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#eef3f7] text-xs text-text-muted'} aria-hidden="true">
                  {rule.passed ? '✓' : '○'}
                </span>
                <span>{rule.label}</span>
              </li>
            ))}
          </ul>

          <label className="flex flex-col gap-2.5">
            <span className="text-[0.8125rem] leading-6 text-text-muted">{t('register:password.confirm.label')}</span>
            <span className="relative flex items-center">
              <input
                className="min-h-[2.125rem] w-full rounded border border-border-soft bg-surface-card px-3 py-[0.4375rem] pr-9 text-[0.8125rem] leading-6 text-text-primary transition-[border-color,box-shadow] duration-150 placeholder:text-[#99a7b5] focus-visible:border-brand-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring"
                maxLength={16}
                onChange={(event) => setConfirmPassword(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') void handleContinue() }}
                placeholder={t('register:password.field.placeholder')}
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
              />
              <button
                aria-label={
                  showConfirmPassword ? t('register:password.hideConfirm') : t('register:password.showConfirm')
                }
                className="absolute right-4 inline-flex items-center border-none bg-transparent p-0 text-[#606975] cursor-pointer focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                onClick={() => setShowConfirmPassword((current) => !current)}
                type="button"
              >
                ◉
              </button>
            </span>
          </label>

          {passwordsMatch ? (
            <p className="text-center text-sm leading-6 text-[var(--color-success,#22c55e)]">{t('register:password.match')}</p>
          ) : passwordsMismatch ? (
            <p className="text-center text-sm leading-6 text-[#e02d3c]" role="alert">
              {t('register:password.mismatch')}
            </p>
          ) : null}
        </div>

        <div className="mt-8 flex w-full max-w-96 items-center justify-center gap-3">
          <RegistrationBackButton />
          <Button className="h-9 w-28 px-5 py-1.5 text-sm disabled:bg-[#f1f1f3] disabled:text-[#8c939b] disabled:opacity-100" disabled={!canContinue} onClick={() => void handleContinue()}>
            {isSubmitting ? t('register:password.btn.saving') : t('register:password.btn.next')}
          </Button>
        </div>
      </AuthEntryCard>

      {showRegisterErrorModal ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-[rgba(5,20,31,0.55)]" />
          <div className="relative z-10 flex w-full max-w-sm flex-col items-center rounded-md bg-white px-6 py-8 text-center shadow-xl">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ffebee] text-2xl font-bold text-[#ff384d]"
              aria-hidden="true"
            >
              !
            </div>
            <h2 className="mt-4 text-base font-bold leading-5 text-text-primary">{t('register:password.error.title')}</h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">
              {t('register:password.error.description')}
            </p>
            <div className="mt-8 w-full">
              <Button
                className="min-h-9 bg-[#ff384d] px-5 py-1.5 text-sm hover:bg-[#e03245] active:bg-[#c92d3e]"
                onClick={() => setShowRegisterErrorModal(false)}
              >
                {t('register:password.error.btn')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
